// Classic desktop nav overflow (#417): how many links the row shows.
//
// When every link fits beside the logo, the row renders exactly as before.
// Otherwise the tail collapses into a "More" menu so the sport switcher stays
// on screen. The rules:
//
// - Observe, don't force. A ResizeObserver on the row, logo, link group and
//   switcher runs a read-only test after the browser's own layout. A row that
//   fits stops there: no state change, no timer, no write.
// - Never collapse on a transient. A cold load restores the stored sport
//   (basketball → soccer) and loads the menu for both, so a row can overflow
//   for a moment and then fit. An overflow is acted on only once the final
//   fonts are in and it has held, with no resize or link change, for
//   NAV_SETTLE_MS.
// - Decide from fresh measurements of every link (no width cache). While
//   collapsed, any change to the inputs (the link set, the row or switcher
//   width, a font load) re-expands and judges again within one frame, so a
//   row that now fits always ends uncollapsed.
// - Never change state inside the observer callback (that would trip the
//   observer's loop limit): decisions run in a timer or an animation frame.
// - Show no half-made row. Every page draws its own Navigation, so a collapse
//   is remembered for the next mount (same links, same window width), which
//   starts collapsed and judges again before painting anything else. With
//   nothing to go on (a fresh load, when the fonts aren't in), a row whose
//   first look overflows stays hidden until it is judged, at most
//   NAV_HOLD_MAX_MS: only `visibility` changes, so nothing resizes, and a row
//   that fits is never touched. Later overflows (a window being resized) stay
//   visible, as before. The observer starts in a layout effect so that first
//   look comes before the first paint.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { NAV_HOLD_MAX_MS, NAV_LINK_GAP, NAV_MORE_ESTIMATE, NAV_SETTLE_MS, fitNavItems } from './navOverflow'

/** The last collapse, for the next mount. Null: nothing to start from. */
let lastCollapse: { key: string; shown: number } | null = null

/** Forget the last collapse. */
export function forgetNavCollapse() {
  lastCollapse = null
}

const collapseKey = (linksKey: string) => `${linksKey}|${window.innerWidth}`

/**
 * An element's unshrunk width: flex may squeeze the logo (wrapping the
 * wordmark) when the row is tight. Read only for an overflowing row, with
 * shrinking switched off for the read and restored before anything paints.
 */
function naturalWidth(el: HTMLElement): number {
  const hadStyle = el.hasAttribute('style')
  const prev = el.style.flexShrink
  el.style.flexShrink = '0'
  const width = el.getBoundingClientRect().width
  if (prev) el.style.flexShrink = prev
  else el.style.removeProperty('flex-shrink')
  // Leave no empty style="" behind on an element that had none.
  if (!hadStyle && el.getAttribute('style') === '') el.removeAttribute('style')
  return width
}

/** Web fonts still loading: text widths aren't final, so don't judge overflow yet. */
function fontsPending(): boolean {
  return typeof document !== 'undefined' && !!document.fonts && document.fonts.status !== 'loaded'
}

interface Controller {
  /** ResizeObserver callback. */
  observed: () => void
  /** The link set changed (from an effect: reads nothing). */
  linksChanged: () => void
  /** A web font finished loading. */
  fontLoaded: () => void
  start: () => void
  stop: () => void
}

/**
 * `pinned`: the index in `links` of the link that never goes under More (-1
 * for none); `shown` then counts the other links (navOverflow.ts › splitNavLinks).
 */
export function useNavOverflow(links: ReadonlyArray<{ href: string; label: string }>, pinned = -1) {
  const rowRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLAnchorElement>(null)
  const groupRef = useRef<HTMLDivElement>(null)
  const switcherRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const linksKey = links.map((l) => `${l.href}\u0001${l.label}`).join('\u0002')
  const [shown, setShown] = useState(() => (lastCollapse?.key === collapseKey(linksKey) ? lastCollapse.shown : Infinity))

  // Latest values for the controller, which runs outside React's render.
  const linksKeyRef = useRef(linksKey)
  linksKeyRef.current = linksKey
  const pinnedRef = useRef(pinned)
  pinnedRef.current = pinned
  const shownRef = useRef(shown)
  shownRef.current = shown

  const ctlRef = useRef<Controller | null>(null)
  if (!ctlRef.current) {
    let settle: ReturnType<typeof setTimeout> | undefined
    let frame: number | undefined
    let waitingFonts = false
    let fontsEpoch = 0
    let collapsedSig: string | null = null
    let moreWidth = NAV_MORE_ESTIMATE
    let alive = false
    let looked = false
    let hold: ReturnType<typeof setTimeout> | undefined

    const commitShown = (n: number) => {
      if (n === shownRef.current) return
      // Synchronously, so the DOM and shownRef are current for a re-measure.
      flushSync(() => setShown(n))
      shownRef.current = n
    }
    // Show the row again, leaving no empty style="" behind.
    const reveal = () => {
      if (hold === undefined) return
      clearTimeout(hold)
      hold = undefined
      const row = rowRef.current
      if (!row) return
      row.style.removeProperty('visibility')
      if (row.getAttribute('style') === '') row.removeAttribute('style')
    }
    const conceal = () => {
      const row = rowRef.current
      if (!row || hold !== undefined) return
      row.style.visibility = 'hidden'
      hold = setTimeout(reveal, NAV_HOLD_MAX_MS)
    }
    const layout = () => {
      const row = rowRef.current
      const logo = logoRef.current
      const group = groupRef.current
      const switcher = switcherRef.current
      if (!row || !logo || !group || !switcher) return null
      const rowRect = row.getBoundingClientRect()
      // Not laid out, or below lg (the desktop row is display:none and the drawer lists everything).
      if (rowRect.width === 0 || group.offsetWidth === 0) return null
      return { logo, group, switcher, rowRect }
    }
    type Layout = NonNullable<ReturnType<typeof layout>>
    // Does the row genuinely overflow: past its right edge, clipped, or into the logo?
    const overflows = ({ group, logo, rowRect }: Layout) => {
      const groupRect = group.getBoundingClientRect()
      return (
        groupRect.right > rowRect.right + 0.5 ||
        group.scrollWidth > group.clientWidth + 1 ||
        logo.getBoundingClientRect().right > groupRect.left + 0.5
      )
    }
    // The inputs a collapsed row was judged with.
    const signature = ({ switcher, rowRect }: Layout) =>
      `${linksKeyRef.current}|${Math.round(rowRect.width)}|${Math.round(switcher.getBoundingClientRect().width)}|${fontsEpoch}`
    // With every link rendered: the longest prefix that fits next to the logo at its natural width.
    const judge = ({ logo, group, switcher, rowRect }: Layout) => {
      const widths = Array.from(group.querySelectorAll<HTMLElement>(':scope > a'), (el) => el.getBoundingClientRect().width)
      const switcherOuter = switcher.getBoundingClientRect().width + (parseFloat(getComputedStyle(switcher).marginLeft) || 0)
      const fit = fitNavItems(widths, rowRect.width - naturalWidth(logo) - switcherOuter - NAV_LINK_GAP, moreWidth, NAV_LINK_GAP, pinnedRef.current)
      return fit >= widths.length ? Infinity : fit
    }
    // Every link is rendered and the row overflows: collapse to what fits.
    const collapseToFit = (l: Layout) => {
      let n = judge(l)
      commitShown(n)
      if (n !== Infinity && moreRef.current) {
        // First collapse: swap the More estimate for its real width and judge again.
        const real = moreRef.current.getBoundingClientRect().width
        if (Math.abs(real - moreWidth) > 0.5) {
          moreWidth = real
          commitShown(Infinity)
          const fresh = layout()
          n = fresh ? judge(fresh) : Infinity
          commitShown(n)
        }
      }
      const after = n === Infinity ? null : layout()
      collapsedSig = after ? signature(after) : null
      lastCollapse = after ? { key: collapseKey(linksKeyRef.current), shown: n } : null
    }
    const inFrame = (fn: () => void) => {
      if (frame !== undefined) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = undefined
        if (alive) fn()
      })
    }
    const waitForFonts = () => {
      if (waitingFonts || !document.fonts?.ready) return
      waitingFonts = true
      document.fonts.ready.then(() => {
        waitingFonts = false
        if (alive) inFrame(() => ctl.observed())
      })
    }
    // Overflowing while every link shows: act only once it has held still.
    const scheduleSettle = () => {
      if (settle !== undefined) clearTimeout(settle)
      settle = setTimeout(() => {
        settle = undefined
        inFrame(() => {
          if (shownRef.current !== Infinity) return
          if (fontsPending()) return waitForFonts()
          const l = layout()
          if (l && overflows(l)) collapseToFit(l)
          reveal()
        })
      }, NAV_SETTLE_MS)
    }
    const cancelSettle = () => {
      if (settle !== undefined) clearTimeout(settle)
      settle = undefined
    }
    // Collapsed: when an input changed since collapsing, show everything and judge again.
    const reconcile = () =>
      inFrame(() => {
        if (shownRef.current === Infinity) return
        if (fontsPending()) return waitForFonts()
        const l = layout()
        if (!l || signature(l) === collapsedSig) return
        commitShown(Infinity)
        collapsedSig = null
        lastCollapse = null
        const fresh = layout()
        if (fresh && overflows(fresh)) collapseToFit(fresh)
      })

    const ctl: Controller = {
      observed: () => {
        const firstLook = !looked
        looked = true
        if (shownRef.current !== Infinity) return reconcile()
        const l = layout()
        // The fitting path: three rects read after layout, nothing else.
        const over = !!l && overflows(l)
        if (firstLook && over) conceal()
        if (fontsPending()) return waitForFonts()
        if (over) return scheduleSettle()
        cancelSettle()
        reveal()
      },
      linksChanged: () => {
        if (settle !== undefined) scheduleSettle()
        if (shownRef.current !== Infinity) reconcile()
      },
      fontLoaded: () => {
        fontsEpoch += 1
        if (shownRef.current !== Infinity) reconcile()
      },
      start: () => { alive = true },
      stop: () => {
        alive = false
        // Not reveal(): a Suspense boundary re-suspending also lands here, with
        // the row kept. The hold's own timer ends it whatever happens.
        cancelSettle()
        if (frame !== undefined) cancelAnimationFrame(frame)
        frame = undefined
      },
    }
    ctlRef.current = ctl
  }

  useLayoutEffect(() => {
    const ctl = ctlRef.current!
    const els = [rowRef.current, logoRef.current, groupRef.current, switcherRef.current]
    if (els.some((el) => !el) || typeof ResizeObserver === 'undefined') return
    ctl.start()
    const observer = new ResizeObserver(() => ctl.observed())
    els.forEach((el) => observer.observe(el as Element))
    const fonts = typeof document !== 'undefined' ? document.fonts : undefined
    const onFont = () => ctl.fontLoaded()
    fonts?.addEventListener?.('loadingdone', onFont)
    return () => {
      observer.disconnect()
      fonts?.removeEventListener?.('loadingdone', onFont)
      ctl.stop()
    }
  }, [])

  // A new link set (e.g. the sport's own menu arrived) can turn an overflow
  // into a fit without resizing anything observed.
  useEffect(() => {
    ctlRef.current!.linksChanged()
  }, [linksKey])

  return { rowRef, logoRef, groupRef, switcherRef, moreRef, shown }
}
